import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { SearchAPI } from '../../../../utils/api';
import { useAuth } from '../../../AuthProvider';
import { isAdminOnlyDoc, isMissionNotesDoc } from '../../../../utils/docTags';
import { DEFAULT_VIEWS } from './constants';
import SearchHeader from './components/SearchHeader';
import CampaignColumn from './components/CampaignColumn';
import MissionColumn from './components/MissionColumn';
import DocColumn from './components/DocColumn';

// Import styles in correct order
import './styles/layout.css';
import './styles/search-header.css';
import './styles/items.css';
import './styles/view-menu.css';

/**
 * UnifiedSearchPopup Component
 * Three-column layout: Campaigns | Missions | Documents
 * Campaigns/missions via backend API; documents filtered client-side by title.
 */
const UnifiedSearchPopup = ({
  isOpen,
  onClose,
  campaigns = [],
  allSprints = [],
  docs = [],
  onSelectCampaign,
  onSelectMission,
  onSelectDoc,
  position = { top: 0, left: 0 },
}) => {
  const { role } = useAuth();
  const canViewAdminDocs = (role ?? 'admin') !== 'user';
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState({ campaigns: [], missions: [] });
  const [openViewMenuId, setOpenViewMenuId] = useState(null);
  const [viewMenuPosition, setViewMenuPosition] = useState(null);
  const popupRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Get views for a specific campaign
  const getViewsForCampaign = useCallback((campaignId) => {
    const campaign = allSprints.find(s => s.id === campaignId);
    const customViews = campaign?.views || [];
    
    const views = [...DEFAULT_VIEWS];
    customViews.forEach(cv => {
      views.push({
        id: cv.id,
        type: cv.type,
        name: cv.name,
        isDefault: false,
      });
    });
    return views;
  }, [allSprints]);

  // Perform search via backend API
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults({ campaigns: [], missions: [] });
      setIsLoading(false);
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const result = await SearchAPI.search({ 
        query: query.trim(), 
        scope: 'all',
        limit: 50 
      });
      
      setSearchResults({
        campaigns: result.campaigns || [],
        missions: result.missions || []
      });
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') return;
      
      console.error('Search error:', err);
      setError(err.message || 'Search failed');
      setSearchResults({ campaigns: [], missions: [] });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults({ campaigns: [], missions: [] });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(() => {
      performSearch(searchQuery);
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, performSearch]);

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults({ campaigns: [], missions: [] });
      setError(null);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [isOpen]);

  // Close on click outside or escape
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Handle campaign click
  const handleCampaignClick = useCallback((campaign) => {
    const fullCampaign = allSprints.find(s => s.id === campaign.id) || campaign;
    onSelectCampaign?.(fullCampaign, null);
    onClose();
  }, [allSprints, onSelectCampaign, onClose]);

  // Handle campaign view selection from dropdown
  const handleCampaignViewSelect = useCallback((campaign, view) => {
    const fullCampaign = allSprints.find(s => s.id === campaign.id) || campaign;
    const viewInfo = view.isDefault ? view.type : { customViewId: view.id, type: view.type };
    onSelectCampaign?.(fullCampaign, viewInfo);
    onClose();
  }, [allSprints, onSelectCampaign, onClose]);

  // Handle mission click
  const handleMissionClick = useCallback((mission) => {
    const campaign = allSprints.find(c => c.id === mission.campaignId) || { id: mission.campaignId, name: mission.campaignName };
    onSelectMission?.(mission, campaign, null);
    onClose();
  }, [allSprints, onSelectMission, onClose]);

  // Handle mission view selection from dropdown
  const handleMissionViewSelect = useCallback((mission, view) => {
    const campaign = allSprints.find(c => c.id === mission.campaignId) || { id: mission.campaignId, name: mission.campaignName };
    const viewInfo = view.isDefault ? view.type : { customViewId: view.id, type: view.type };
    onSelectMission?.(mission, campaign, viewInfo);
    onClose();
  }, [allSprints, onSelectMission, onClose]);

  const handleDocClick = useCallback(
    (doc) => {
      onSelectDoc?.(doc);
      onClose();
    },
    [onSelectDoc, onClose]
  );

  // Active docs only — title match, same visibility rules as the library
  const filteredDocuments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];

    const searchable = docs.filter(
      (doc) =>
        !doc.deletedAt &&
        !isMissionNotesDoc(doc) &&
        (canViewAdminDocs || !isAdminOnlyDoc(doc))
    );

    return searchable
      .filter((doc) => (doc.title || '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [docs, searchQuery, canViewAdminDocs]);

  // Toggle view menu for an item
  const handleToggleViewMenu = useCallback((e, itemId, itemType) => {
    e.stopPropagation();
    const menuKey = `${itemType}-${itemId}`;
    if (openViewMenuId === menuKey) {
      setOpenViewMenuId(null);
      setViewMenuPosition(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuWidth = 200;
      setOpenViewMenuId(menuKey);
      setViewMenuPosition({
        top: rect.bottom + 4,
        left: Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 16),
        width: menuWidth,
      });
    }
  }, [openViewMenuId]);

  // Close view menu when clicking outside
  useEffect(() => {
    if (!openViewMenuId) return;
    const handleClick = () => {
      setOpenViewMenuId(null);
      setViewMenuPosition(null);
    };
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
    };
  }, [openViewMenuId]);

  if (!isOpen) return null;

  const { campaigns: filteredCampaigns, missions: filteredMissions } = searchResults;

  return createPortal(
    <>
      {/* Backdrop blur overlay */}
      <div className="unified-search-popup__backdrop" onClick={onClose} />
      
      <div ref={popupRef} className="unified-search-popup">
        <SearchHeader
          inputRef={inputRef}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />

        {/* Error State */}
        {error && (
          <div className="unified-search-popup__error">
            {error}
          </div>
        )}

        {/* Two Column Layout */}
        <div className="unified-search-popup__columns">
          <CampaignColumn
            campaigns={filteredCampaigns}
            isLoading={isLoading}
            searchQuery={searchQuery}
            openViewMenuId={openViewMenuId}
            viewMenuPosition={viewMenuPosition}
            getViewsForCampaign={getViewsForCampaign}
            onCampaignClick={handleCampaignClick}
            onCampaignViewSelect={handleCampaignViewSelect}
            onToggleViewMenu={handleToggleViewMenu}
          />
          <MissionColumn
            missions={filteredMissions}
            isLoading={isLoading}
            searchQuery={searchQuery}
            openViewMenuId={openViewMenuId}
            viewMenuPosition={viewMenuPosition}
            getViewsForCampaign={getViewsForCampaign}
            onMissionClick={handleMissionClick}
            onMissionViewSelect={handleMissionViewSelect}
            onToggleViewMenu={handleToggleViewMenu}
          />
          <DocColumn
            documents={filteredDocuments}
            searchQuery={searchQuery}
            onDocClick={handleDocClick}
          />
        </div>
      </div>
    </>,
    document.body
  );
};

export default UnifiedSearchPopup;
